import { useState, useEffect } from "react";
import {
  FieldGroup,
  FormDialog,
  InputField,
  SelectField,
} from "@/components/ui/form/form-dialog";

import { useConvexForm } from "@/hooks/useFormValidation";
import { useToastMutation } from "@/hooks/useToastMutation";
import { Infer, v } from "convex/values";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { validatorUsers } from "@/convex/system/users";
import { Label } from "@/components/ui/label";
import BranchMultiSelect from "@/components/branches/branch-multi-select";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: Doc<"users">;
}

// Códigos de región más comunes
const COUNTRY_CODES = [
  { code: "+504", country: "Honduras", flag: "🇭🇳" },
  { code: "+1", country: "Estados Unidos/Canadá", flag: "🇺🇸" },
  { code: "+52", country: "México", flag: "🇲🇽" },
  { code: "+503", country: "El Salvador", flag: "🇸🇻" },
  { code: "+502", country: "Guatemala", flag: "🇬🇹" },
  { code: "+505", country: "Nicaragua", flag: "🇳🇮" },
  { code: "+506", country: "Costa Rica", flag: "🇨🇷" },
  { code: "+507", country: "Panamá", flag: "🇵🇦" },
];

export default function UsersModal({ isOpen, onClose, data }: UserModalProps) {
  const fieldsWithoutIds = validatorUsers
    .omit("branchIds", "internalControl")
    .extend({
      countryCode: v.union(...COUNTRY_CODES.map((c) => v.literal(c.code))),
    }).fields;
  type UserFormData = Omit<Infer<typeof validatorUsers>, "branchIds"> & {
    countryCode: string;
  };

  // State for selected branch IDs (access permissions)
  const [selectedBranchIds, setSelectedBranchIds] = useState<Id<"branches">[]>(
    [],
  );

  // Initialize selectedBranchIds when modal opens
  useEffect(() => {
    if (isOpen) {
      if (data?.branchIds) {
        setSelectedBranchIds(data.branchIds);
      } else {
        setSelectedBranchIds([]);
      }
    }
  }, [isOpen, data]);

  const { execute: updateUser } = useToastMutation(
    api.system.users.mutations.updateProfile,
  );
  const { execute: createUser } = useToastMutation(
    api.system.users.mutations.prepareCreateUser,
    {
      errorMessage: (error) => `Error al crear el usuario: ${error}`,
    },
  );

  const allRoles = useQuery(api.system.roles.queries.getAllRoles);
  const allBranches = useQuery(api.companies.branches.queries.getAllBranches);

  const { handleSubmit, isLoading, formId, useSelectMapping, watch } =
    useConvexForm<typeof fieldsWithoutIds, UserFormData>({
      isUpdate: !!data,
      validator: fieldsWithoutIds,
      mode: "hybrid",
      initialData: data ? data : undefined,
      initialDataId: data?._id,

      customValidations: [
        {
          field: "type",
          validate: (value) => value !== undefined && value !== "",
          message: "El tipo de rol es requerido",
        },
      ],
      parsers: {
        phone: {
          // DB → formulario (ej: convertir "+504 123-4567" → "5041234567")
          parse: (dbValue: string) =>
            (countryCode ?? "") + " " + (dbValue ?? "").replace(/\D/g, ""),
          // // Formulario → DB (si quieres guardar solo dígitos)
          format: (formValue: string) =>
            (countryCode ?? "") + " " + (formValue ?? "").replace(/\D/g, ""),
          // // Opcional: validación en el form antes de formatear
          // validate: (formValue: string) => /^[0-9]*$/.test(formValue),
          getValidationMessage: () => "El teléfono solo debe contener dígitos",
        },
      },
      onSubmit: async (formData, isUpdate) => {
        const { countryCode, ...all } = formData;

        isUpdate && data
          ? await updateUser({
              id: data._id,
              ...all,
              branchIds: selectedBranchIds,
            })
          : await createUser({
              ...all,
              branchIds: selectedBranchIds,
            });
      },
      onSuccess: () => onClose(),
    });

  const countryCode: string = watch("countryCode") || "+504";
  const getDefaultPhone = (phone: string | undefined) => {
    const splitLength = phone?.split(" ").length || 0;
    if (splitLength > 1) {
      return phone?.split(" ")[1];
    }
    if (splitLength === 1 && phone) {
      return phone?.split(" ")[0];
    }
    return "";
  };

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      title={data ? "Editar Usuario" : "Nuevo Usuario"}
      description={
        data
          ? `Modificar información de "${data.name}"`
          : "Agregar un nuevo usuario al sistema"
      }
      onSubmit={handleSubmit}
      isLoading={isLoading}
      submitLabel={data ? "Actualizar Usuario" : "Crear Usuario"}
      maxWidth={"sm"}
      formId={formId}
    >
      <div className={`grid grid-cols-1  gap-4`}>
        <FieldGroup title="Información general">
          <InputField
            icon="User"
            label="Nombre"
            name="name"
            defaultValue={data?.name || ""}
            placeholder="Nombre del usuario"
            required
          />

          <InputField
            icon="Mail"
            label="Email"
            type="email"
            name="email"
            defaultValue={data?.email || ""}
            placeholder="Email del usuario"
            required
          />

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Sucursales con Acceso
              <span className="text-destructive ml-1">*</span>
            </Label>
            <BranchMultiSelect
              selectedBranchIds={selectedBranchIds}
              onSelectionChange={setSelectedBranchIds}
              required
            />
          </div>

          <SelectField
            required
            label="Sucursal Predeterminada"
            name="defaultBranchId"
            className="w-fit"
            {...useSelectMapping({
              fieldName: "defaultBranchId",
              defaultValue: data?.defaultBranchId || "",
            })}
            options={
              allBranches
                ?.filter((branch) => selectedBranchIds.includes(branch._id))
                .map((v) =>
                  Object({
                    label: v.name,
                    value: v._id,
                  }),
                ) || []
            }
            helperText={
              selectedBranchIds.length === 0
                ? "Primero selecciona las sucursales con acceso"
                : undefined
            }
          />

          <FieldGroup title="Teléfono" columns={2}>
            <SelectField
              required
              label="Code"
              name="countryCode"
              className="w-fit"
              {...useSelectMapping({
                fieldName: "countryCode",
                defaultValue: data?.phone?.split(" ")[0] || "+504",
              })}
              options={COUNTRY_CODES.map((v) =>
                Object({
                  label: `(${v.flag} ${v.code})`,
                  value: v.code,
                }),
              )}
            />
            <InputField
              icon="Phone"
              label="Teléfono"
              name="phone"
              defaultValue={getDefaultPhone(data?.phone)}
              placeholder="Teléfono del usuario"
            />
          </FieldGroup>

          <SelectField
            required
            icon="Key"
            label="Rol"
            name="roleId"
            {...useSelectMapping({
              fieldName: "roleId",
              defaultValue:
                data?.roleId ||
                allRoles?.find((role) => role.type === "company_user")?._id ||
                "",
            })}
            options={
              allRoles?.map((role) => ({
                value: role._id,
                label: role.name,
              })) || []
            }
          />
        </FieldGroup>
      </div>
    </FormDialog>
  );
}
